import { Component, Inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { ActivatedRoute } from '@angular/router';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { CognitoLanguageMappingService } from 'src/app/services/general/cognito-language-mapping.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'login',
    },
  ],
})
export class LoginComponent {
  readonly showSsoLogin = signal(false);
  readonly isEmailError = signal(false);
  readonly translationLoaded = signal(false);
  readonly showRegionRecoveryMessage = signal(false);

  email: string = '';
  error: string = '';
  wrongRegionMessage: string = '';
  wrongRegionLink: string = '';
  locale: string = 'en';

  readonly alternateRegionLoginUrl = environment.alternateRegionLoginUrl;
  readonly alternateRegionName = environment.alternateRegionName;

  constructor(
    private amplitudeService: AmplitudeService,
    private route: ActivatedRoute,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private cognitoLanguageMappingService: CognitoLanguageMappingService,
    private featureFlagsService: FeatureFlagsService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    this.translationService.loadScopedTranslation('login').then(() => {
      this.error = this.translocoService.translate('EMAIL_ERROR', {}, 'login');
      this.wrongRegionMessage = this.translocoService.translate('WRONG_REGION_MESSAGE', {}, 'login');
      this.wrongRegionLink = this.translocoService.translate('WRONG_REGION_LINK', { regionName: this.alternateRegionName }, 'login');
      this.translationLoaded.set(true);
    });
    this.locale = this.translationService.findLocale();
    this.amplitudeService.trackEvent('login_pageview');

    this.featureFlagsService.initPreAuth();

    this.route.queryParams.subscribe(async (params) => {
      if (params['error_code'] === '400') {
        this.toggleSsoLogin();

        const flagEnabled = await this.featureFlagsService.isFlagEnabled(
          AppConstants.WRONG_REGION_LOGIN_RECOVERY
        );
        this.showRegionRecoveryMessage.set(flagEnabled && !!this.alternateRegionLoginUrl);
        this.isEmailError.set(true);
      }
    });
  }

  login() {
    this.amplitudeService.trackEvent('login_click_password');
    localStorage.setItem('is_sso_user', 'false');
    const cognitoLanguage = this.cognitoLanguageMappingService.mapToCognitoSupportedLanguage(this.locale);
    window.location.href = environment.backendUrl + '/login?lang=' + cognitoLanguage;
  }

  toggleSsoLogin() {
    this.showSsoLogin.update(v => !v);
  }

  ssoLogin(email: string) {
    if (email.length > 0) {
      this.amplitudeService.trackEvent('login_click_sso');
      localStorage.setItem('is_sso_user', 'true');
      this.document.location.href = environment.backendUrl + `/login?email=${encodeURIComponent(email)}`;
    }
  }

  validateEmail() {
    if (this.email.length === 0) {
      this.isEmailError.set(false);
    }
  }

  get consentMessage(): string {
    return this.translocoService.translate('login.LOGIN_CONSENT_MESSAGE', {
      termsLink: this.createLink(
        'https://quantified.ai/terms/',
        this.translocoService.translate('login.TERMS_OF_SERVICE')
      ),
      privacyLink: this.createLink(
        'https://quantified.ai/quantified-communications-inc-privacy-policy/',
        this.translocoService.translate('login.PRIVACY_POLICY')
      ),
    });
  }

  createLink(url: string, text: string): string {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="brand-link">${text}</a>`;
  }
}
